import { fetchApi, type GenericRes } from '@hunk/shared/api/fetchApi';

/**
 * Content pages (about, guides...) - Markdown edited in the admin (/admin/pages). The first content comes from
 * apps/server/seed/pages/<key>.md. Paths keep the old hunk.co.il addresses. Each page can be shown in the top line
 * (the red strip above the header, like bb365) - on by default, switched per page in the admin.
 */
export const SITE_PAGES = [
  { key: 'about', path: '/about', title: 'אודותינו' },
  { key: 'guides', path: '/מדריכים-והסברים', title: 'מדריכים והסברים' },
  { key: 'returns', path: '/מדיניות-החזרות', title: 'מדיניות החזרות' },
  { key: 'privacy', path: '/מדיניות-פרטיות', title: 'מדיניות פרטיות' },
  { key: 'accessibility', path: '/הצהרת-נגישות', title: 'הצהרת נגישות' },
  { key: 'contact', path: '/צור-קשר', title: 'צור קשר' }
] as const;
/** the accessibility statement (linked from the accessibility menu) */
export const ACCESSIBILITY_PATH = '/הצהרת-נגישות';
export type SitePageKey = (typeof SITE_PAGES)[number]['key'];
export const isSitePageKey = (k: unknown): k is SitePageKey => SITE_PAGES.some((p) => p.key === k);

export interface SitePage {
  key: SitePageKey;
  path: string;
  title: string;
  markdown: string;
  /** shown in the top line (red strip above the header) */
  inTopLine: boolean;
  /** ISO, null = still the seed content */
  updatedOn: string | null;
}

export interface SitePageRes extends GenericRes {
  page?: SitePage;
}
export interface SitePagesRes extends GenericRes {
  pages?: SitePage[];
}
export interface SaveSitePageReq {
  title: string;
  markdown: string;
  inTopLine: boolean;
}

/** what the header / footer need (in the public catalog): every page with its current title */
export type SitePageLink = Pick<SitePage, 'key' | 'path' | 'title' | 'inTopLine'>;

type H = { headers?: HeadersInit };

/** public: one page */
export const getPage = (key: SitePageKey) => fetchApi<{}, SitePageRes>({ apiPath: `/api/shop/page/${key}` });

/** admin: every page / save one / back to the seed content */
export const adminGetPages = ({ headers }: H) => fetchApi<{}, SitePagesRes>({ apiPath: '/api/admin/pages', headers });
export const adminSavePage = (key: SitePageKey, data: SaveSitePageReq, { headers }: H) =>
  fetchApi<SaveSitePageReq, SitePageRes>({ apiPath: `/api/admin/pages/${key}`, method: 'PUT', data, headers });
export const adminResetPage = (key: SitePageKey, { headers }: H) => fetchApi<{}, SitePageRes>({ apiPath: `/api/admin/pages/${key}`, method: 'DELETE', headers });
